import { Component, type ReactNode, useState } from 'react';
import './remixicon.css';
import { useI18n } from './i18n';
import { buildHash, pick, type BiText } from './i18n-core';

/* ------------------------------------------------------------------ *
 * [gemini-icons-remix] 统一 Remix Icon 组件封装与导出
 * ------------------------------------------------------------------ */
export function RiIcon({
  name,
  className = '',
  'aria-hidden': ariaHidden = true,
}: {
  name: string;
  className?: string;
  'aria-hidden'?: boolean;
}) {
  const iconClass = name.startsWith('ri-') ? name : `ri-${name}`;
  return <i className={`${iconClass} ${className}`} aria-hidden={ariaHidden} />;
}

/* ------------------------------------------------------------------ *
 * 企业官网框架：页眉 / 页脚 / 版块 / 图片。
 * 供集团门户与各子公司站共用。以新增方式扩展，不改变既有组件语义。
 * [gemini-style-modern] 现代化视觉与微交互升级
 * ------------------------------------------------------------------ */

export interface SubsidiaryLink {
  key: string;
  name: BiText;
  url: string;
}

export interface NavEntry {
  to: string;
  label: BiText;
  /**
   * 外部站点直链（独立域名，如票酷 t.hastur.cn）。
   * 存在时渲染为普通链接并新开标签，不参与站内高亮与哈希路由。
   */
  href?: string;
  /** 下拉子项（如业务生态直达各子公司）。存在时渲染为下拉菜单。 */
  children?: SubsidiaryLink[];
}

const SUB_ICONS: Record<string, string> = {
  media: 'ri-movie-2-line',
  tech: 'ri-cpu-line',
  health: 'ri-heart-pulse-line',
  logistics: 'ri-truck-line',
  invest: 'ri-funds-line',
  products: 'ri-stack-line',
};

/** 站内跳转：与 NavLink 同一套哈希路由，中英切换保持当前语言。 */
export function GoLink({
  to,
  className = '',
  children,
}: {
  to: string;
  className?: string;
  children: ReactNode;
}) {
  const { locale, navigate } = useI18n();
  return (
    <a
      href={`#${buildHash(locale, to)}`}
      className={className}
      onClick={(e) => {
        e.preventDefault();
        navigate(to);
      }}
    >
      {children}
    </a>
  );
}

function NavItemLabel({ entry, active }: { entry: NavEntry; active: boolean }) {
  const { locale } = useI18n();
  const label = pick(entry.label, locale);
  return (
    <span className={active ? 'font-semibold text-primary-light' : 'text-body transition-colors hover:text-primary-light'}>
      {label}
    </span>
  );
}

/** 页眉：品牌 + 一级导航（桌面）/ 折叠菜单（移动端）+ 语言切换由调用方传入。 */
export function SiteHeader({
  brand,
  nav,
  localeToggle,
  homeLabel,
}: {
  /** mark 为 RemixIcon 类名（如 ri-movie-2-line），不再是文字字标 */
  brand: { name: BiText; mark: string };
  nav: NavEntry[];
  localeToggle: ReactNode;
  homeLabel: BiText;
}) {
  const { locale, path } = useI18n();
  // 外部直链不参与站内高亮
  const internalNav = nav.filter((e) => !e.href);
  const exact = internalNav.find((e) => e.to === path)?.to;
  const activeTo = exact ?? internalNav.find((e) => e.to !== '/' && path.startsWith(e.to + '/'))?.to;
  return (
    <header className="sticky top-0 z-40 border-b border-line/80 bg-bg/90 backdrop-blur-md transition-all">
      <div className="mx-auto flex max-w-6xl items-center justify-between gap-4 px-5 py-3">
        <GoLink to="/" className="group flex items-center gap-3">
          <span
            aria-hidden="true"
            className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary text-on-dark shadow-sm transition-transform group-hover:scale-105"
          >
            <RiIcon name={brand.mark} className="text-lg" />
          </span>
          <span className="text-sm font-semibold tracking-tight text-body transition-colors group-hover:text-primary-light">
            {pick(brand.name, locale)}
            <span className="sr-only"> · {pick(homeLabel, locale)}</span>
          </span>
        </GoLink>

        <nav aria-label={pick({ zh: '主导航', en: 'Main navigation' }, locale)} className="hidden items-center gap-1 xl:flex">
          {nav.map((entry) =>
            entry.href ? (
              <a
                key={entry.to}
                href={entry.href}
                target="_blank"
                rel="noopener noreferrer"
                className="rounded-md px-3 py-2 text-sm transition-colors hover:bg-surface-2"
              >
                <span className="inline-flex items-center gap-1 text-body transition-colors hover:text-primary-light">
                  {pick(entry.label, locale)}
                  <RiIcon name="ri-external-link-line" className="text-xs text-muted" />
                </span>
              </a>
            ) : entry.children ? (
              <div key={entry.to} className="group relative">
                <GoLink to={entry.to} className="inline-flex items-center rounded-md px-3 py-2 text-sm transition-colors hover:bg-surface-2">
                  <NavItemLabel entry={entry} active={activeTo === entry.to} />
                  <RiIcon name="ri-arrow-down-s-line" className="ml-1 text-xs text-muted transition-transform duration-200 group-hover:rotate-180" />
                </GoLink>
                <div className="invisible absolute left-0 top-full z-50 w-72 rounded-xl border border-line bg-bg p-2 shadow-xl ring-1 ring-black/5 transition-all duration-200 group-focus-within:visible group-hover:visible">
                  {entry.children.map((c) => (
                    <a
                      key={c.key}
                      href={c.url}
                      className="group/item flex items-center gap-2.5 rounded-lg px-3 py-2.5 text-sm text-body transition-all hover:bg-surface-2 hover:text-primary-light"
                    >
                      <RiIcon name={SUB_ICONS[c.key] || 'ri-building-line'} className="text-base text-primary/70 transition-transform group-hover/item:scale-110" />
                      <span className="font-medium">{pick(c.name, locale)}</span>
                    </a>
                  ))}
                </div>
              </div>
            ) : (
              <GoLink key={entry.to} to={entry.to} className="rounded-md px-3 py-2 text-sm transition-colors hover:bg-surface-2">
                <NavItemLabel entry={entry} active={activeTo === entry.to} />
              </GoLink>
            ),
          )}
          <span className="mx-2 h-4 w-px bg-line" aria-hidden="true" />
          {localeToggle}
        </nav>

        <div className="flex items-center gap-2 xl:hidden">
          {localeToggle}
          <details className="relative">
            <summary className="inline-flex cursor-pointer list-none items-center gap-1.5 rounded-lg border border-line bg-surface px-3 py-1.5 text-sm font-medium text-body transition-colors hover:bg-surface-2">
              <RiIcon name="ri-menu-line" className="text-base text-muted" />
              <span>{pick({ zh: '菜单', en: 'Menu' }, locale)}</span>
            </summary>
            <div className="absolute right-0 top-full z-50 mt-2 w-72 rounded-xl border border-line bg-bg p-2 shadow-xl ring-1 ring-black/5">
              {nav.map((entry) => (
                <div key={entry.to}>
                  {entry.href ? (
                    <a
                      href={entry.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-1 rounded-lg px-3 py-2 text-sm font-medium text-body hover:bg-surface-2 hover:text-primary-light"
                    >
                      {pick(entry.label, locale)}
                      <RiIcon name="ri-external-link-line" className="text-xs text-muted" />
                    </a>
                  ) : (
                    <GoLink to={entry.to} className="block rounded-lg px-3 py-2 text-sm font-medium text-body hover:bg-surface-2 hover:text-primary-light">
                      {pick(entry.label, locale)}
                    </GoLink>
                  )}
                  {entry.children?.map((c) => (
                    <a key={c.key} href={c.url} className="flex items-center gap-2 rounded-lg py-1.5 pl-6 pr-3 text-xs text-muted hover:bg-surface-2 hover:text-primary-light">
                      <RiIcon name={SUB_ICONS[c.key] || 'ri-building-line'} className="text-sm text-primary/60" />
                      <span>{pick(c.name, locale)}</span>
                    </a>
                  ))}
                </div>
              ))}
            </div>
          </details>
        </div>
      </div>
    </header>
  );
}

/** 页脚：子公司直达 + 免责声明。 */
export function SiteFooter({
  subsidiaries,
  note,
  site,
}: {
  subsidiaries: SubsidiaryLink[];
  note: BiText;
  site: string;
}) {
  const { locale, t } = useI18n();
  return (
    <footer className="mt-0 border-t border-line bg-surface/80">
      <div className="mx-auto max-w-6xl px-5 py-14">
        <div className="flex items-center gap-2 text-xs font-semibold tracking-widest text-muted">
          <RiIcon name="ri-apps-2-line" className="text-primary text-sm" />
          <span>{pick({ zh: '集团业务生态', en: 'Business Ecosystem' }, locale)}</span>
        </div>
        <ul className="mt-5 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {subsidiaries.map((s) => (
            <li key={s.key}>
              <a
                href={s.url}
                className="group flex items-center justify-between rounded-xl border border-line bg-bg p-3.5 text-sm text-body transition-all duration-200 hover:-translate-y-0.5 hover:border-primary/50 hover:shadow-sm hover:text-primary-light"
              >
                <div className="flex items-center gap-2.5">
                  <RiIcon
                    name={SUB_ICONS[s.key] || 'ri-building-line'}
                    className="text-base text-primary/70 transition-colors group-hover:text-primary"
                  />
                  <span className="font-medium">{pick(s.name, locale)}</span>
                </div>
                <RiIcon
                  name="ri-arrow-right-up-line"
                  className="text-xs text-muted opacity-50 transition-all group-hover:translate-x-0.5 group-hover:-translate-y-0.5 group-hover:opacity-100"
                />
              </a>
            </li>
          ))}
        </ul>
        <div className="mt-12 flex flex-col justify-between gap-4 border-t border-line/60 pt-6 md:flex-row md:items-center">
          <p className="max-w-4xl text-xs leading-relaxed text-muted">{pick(note, locale)}</p>
          <p className="text-xs font-medium text-muted/80 whitespace-nowrap">{site}</p>
        </div>
        {/* 企业法定信息：统一社会信用代码与注册地址（全站页脚统一展示） */}
        <div className="mt-4 border-t border-line/60 pt-4">
          <p className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted/80">
            <span className="font-medium text-muted">{t('legal.name')}</span>
            <span aria-hidden="true" className="text-line-strong">|</span>
            <span>
              {t('legal.taxLabel')}
              <span className="tnum ml-1 tracking-wide">{t('legal.taxId')}</span>
            </span>
            <span aria-hidden="true" className="text-line-strong">|</span>
            <span>{t('legal.address')}</span>
          </p>
        </div>
      </div>
    </footer>
  );
}

/** 版块容器：全宽背景贴满视口，内容限宽居中。 */
export function Section({
  id,
  tone = 'base',
  children,
  className = '',
}: {
  id?: string;
  tone?: 'base' | 'surface' | 'ink' | 'brand';
  children: ReactNode;
  className?: string;
}) {
  const bg = {
    base: 'bg-bg',
    surface: 'bg-surface',
    ink: 'bg-ink text-on-dark',
    brand: 'bg-primary text-on-dark',
  }[tone];
  return (
    <section id={id} aria-labelledby={id ? `${id}-title` : undefined} className={`w-full ${bg} ${className}`}>
      <div className="mx-auto max-w-6xl px-5 py-16 md:py-24">{children}</div>
    </section>
  );
}

/** 版块标题：眉题 + 标题 + 导语。 */
export function SectionTitle({
  id,
  eyebrow,
  title,
  lead,
  tone = 'base',
}: {
  id?: string;
  eyebrow?: string;
  title: string;
  lead?: string;
  tone?: 'base' | 'dark';
}) {
  const dark = tone === 'dark';
  return (
    <header className="max-w-3xl">
      {eyebrow && (
        <div className={`inline-flex items-center gap-1.5 text-xs font-semibold tracking-widest ${dark ? 'text-on-dark/80' : 'text-primary-light'}`}>
          <span className={`h-1.5 w-1.5 rounded-full ${dark ? 'bg-primary-light' : 'bg-primary'}`} />
          {eyebrow}
        </div>
      )}
      <h2 id={id ? `${id}-title` : undefined} className={`mt-3 text-balance text-3xl font-semibold leading-tight tracking-tight md:text-[2.5rem] ${dark ? 'text-on-dark' : 'text-body'}`}>
        {title}
      </h2>
      {lead && <p className={`mt-4 text-base leading-relaxed md:text-lg ${dark ? 'text-on-dark/85' : 'text-muted'}`}>{lead}</p>}
    </header>
  );
}

/** 图片：必须带 alt；可选署名。图片本地化，不热链。 */
export function Figure({
  src,
  alt,
  credit,
  caption,
  ratio = '16 / 9',
  eager = false,
  className = '',
}: {
  src: string;
  alt: string;
  credit?: string;
  caption?: string;
  ratio?: string;
  eager?: boolean;
  className?: string;
}) {
  return (
    <figure className={`group ${className}`}>
      <div className="overflow-hidden rounded-xl bg-surface-2 shadow-sm transition-all duration-300 group-hover:shadow-md" style={{ aspectRatio: ratio }}>
        <img
          src={src}
          alt={alt}
          loading={eager ? 'eager' : 'lazy'}
          decoding="async"
          className="h-full w-full object-cover transition-transform duration-500 ease-out group-hover:scale-105"
        />
      </div>
      {(caption || credit) && (
        <figcaption className="mt-2.5 flex items-center justify-between text-xs leading-relaxed text-muted">
          <span>{caption}</span>
          {credit && (
            <span className="flex items-center gap-1 text-[11px] text-muted/80">
              <RiIcon name="ri-camera-lens-line" className="text-xs" />
              <span>{credit}</span>
            </span>
          )}
        </figcaption>
      )}
    </figure>
  );
}

/** 抽象视觉块：没有素材时用几何图形代替，不用图库照片冒充实景。 */
export function PlaceholderArt({ label, className = '' }: { label: string; className?: string }) {
  return (
    <div
      role="img"
      aria-label={label}
      className={`relative flex items-center justify-center overflow-hidden rounded-xl bg-surface-2 ${className}`}
    >
      <svg viewBox="0 0 320 200" aria-hidden="true" className="absolute inset-0 h-full w-full">
        <circle cx="250" cy="50" r="46" className="fill-primary/15" />
        <rect x="30" y="90" width="70" height="110" className="fill-primary/25" />
        <rect x="110" y="60" width="60" height="140" className="fill-primary/35" />
        <rect x="180" y="110" width="110" height="90" className="fill-primary/20" />
      </svg>
    </div>
  );
}

/** 全宽页面横幅（子页面顶部）。图片可选，压暗遮罩保证白字对比度。 */
export function PageBanner({
  image,
  imageAlt,
  imageCredit,
  eyebrow,
  title,
  lead,
}: {
  image?: string;
  imageAlt?: string;
  imageCredit?: string;
  eyebrow: string;
  title: string;
  lead: string;
}) {
  return (
    <section aria-labelledby="page-title" className="relative w-full overflow-hidden bg-ink text-on-dark">
      {image ? (
        <>
          <img
            src={image}
            alt={imageAlt ?? ''}
            loading="eager"
            decoding="async"
            className="absolute inset-0 h-full w-full object-cover opacity-60 transition-transform duration-700 ease-out hover:scale-105"
          />
          <div aria-hidden="true" className="absolute inset-0 bg-gradient-to-r from-ink/95 via-ink/80 to-ink/40" />
        </>
      ) : (
        <div aria-hidden="true" className="pointer-events-none absolute inset-0 overflow-hidden">
          <div className="absolute -left-1/4 -top-1/2 h-[500px] w-[500px] rounded-full bg-primary/20 blur-[100px]" />
          <div className="absolute -right-1/4 -bottom-1/2 h-[450px] w-[450px] rounded-full bg-primary/10 blur-[90px]" />
          <div className="absolute inset-0 bg-[linear-gradient(to_right,#ffffff08_1px,transparent_1px),linear-gradient(to_bottom,#ffffff08_1px,transparent_1px)] bg-[size:3.5rem_3.5rem]" />
        </div>
      )}
      <div className="relative mx-auto flex max-w-6xl flex-col justify-end px-5 py-20 md:py-32">
        <div className="inline-flex items-center gap-2 text-xs font-semibold tracking-widest text-on-dark/80">
          <span className="h-1.5 w-1.5 rounded-full bg-primary" />
          {eyebrow}
        </div>
        <h1 id="page-title" className="mt-4 max-w-3xl text-4xl font-semibold leading-tight tracking-tight md:text-[3.25rem]">
          {title}
        </h1>
        <p className="mt-5 max-w-2xl text-base leading-relaxed text-on-dark/85 md:text-lg">{lead}</p>
        {imageCredit && (
          <div className="mt-6 flex items-center gap-1.5 text-xs text-on-dark/70">
            <RiIcon name="ri-camera-lens-line" className="text-sm opacity-80" />
            <span>{imageCredit}</span>
          </div>
        )}
      </div>
    </section>
  );
}

/* ------------------------------------------------------------------ *
 * 咨询表单：纯前端，提交后仅显示成功提示，不存储、不发送。接后端时替换 onSubmit。
 * ------------------------------------------------------------------ */

export interface FormField {
  id: string;
  label: BiText;
  type: 'text' | 'email' | 'tel' | 'textarea' | 'select';
  required?: boolean;
  options?: { value: string; label: BiText }[];
  wide?: boolean;
}

export function EnquiryForm({
  idPrefix,
  fields,
  submit,
  success,
  note,
  requiredHint,
  selectPlaceholder,
}: {
  idPrefix: string;
  fields: FormField[];
  submit: BiText;
  success: BiText;
  note: BiText;
  requiredHint: BiText;
  selectPlaceholder: BiText;
}) {
  const { locale } = useI18n();
  const [done, setDone] = useState(false);
  const inputCls =
    'mt-2 w-full rounded-lg border border-line-strong bg-bg px-3.5 py-2.5 text-sm text-body placeholder:text-muted transition-colors focus:border-primary focus:ring-1 focus:ring-primary focus:outline-none';

  return (
    <form
      className="grid gap-5 md:grid-cols-2"
      onSubmit={(e) => {
        e.preventDefault();
        setDone(true);
      }}
    >
      {fields.map((f) => {
        const id = `${idPrefix}-${f.id}`;
        const span = f.wide || f.type === 'textarea' ? 'md:col-span-2' : '';
        return (
          <div key={f.id} className={span}>
            <label htmlFor={id} className="block text-xs font-medium text-body">
              {pick(f.label, locale)}
              {f.required && (
                <span className="ml-1 text-danger-light" title={pick(requiredHint, locale)}>
                  *
                </span>
              )}
            </label>
            {f.type === 'textarea' ? (
              <textarea id={id} required={f.required} rows={4} className={inputCls} />
            ) : f.type === 'select' ? (
              <select id={id} required={f.required} defaultValue="" className={inputCls}>
                <option value="" disabled>
                  {pick(selectPlaceholder, locale)}
                </option>
                {f.options?.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {pick(opt.label, locale)}
                  </option>
                ))}
              </select>
            ) : (
              <input id={id} type={f.type} required={f.required} className={inputCls} />
            )}
          </div>
        );
      })}

      <div className="md:col-span-2 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between pt-2">
        <p className="text-xs leading-relaxed text-muted">{pick(note, locale)}</p>
        <button
          type="submit"
          className="inline-flex items-center justify-center gap-1.5 rounded-lg bg-primary px-6 py-2.5 text-sm font-semibold text-on-dark shadow-sm transition-all hover:bg-primary-light hover:shadow focus:outline-none focus:ring-2 focus:ring-primary/40"
        >
          <RiIcon name="ri-send-plane-fill" className="text-xs" />
          <span>{pick(submit, locale)}</span>
        </button>
      </div>

      {done && (
        <div role="status" className="md:col-span-2 flex items-center gap-2.5 rounded-xl border border-policy/40 bg-policy/10 p-4 text-sm font-medium text-policy-light">
          <RiIcon name="ri-checkbox-circle-fill" className="text-lg text-policy-light" />
          <span>{pick(success, locale)}</span>
        </div>
      )}
    </form>
  );
}

/** 首页 Hero 指标条：一组高亮数字。 */
export interface HeroStat {
  value: string;
  label: BiText;
}

export function HeroSection({
  slogan,
  lead,
  stats,
  cta,
  image,
  imageAlt,
  imageCredit,
}: {
  slogan: BiText;
  lead: BiText;
  stats?: HeroStat[];
  cta?: { label: BiText; to: string };
  image?: string;
  imageAlt?: string;
  imageCredit?: string;
}) {
  const { locale } = useI18n();
  return (
    <section className="relative w-full overflow-hidden bg-ink text-on-dark">
      {image && (
        <>
          <img
            src={image}
            alt={imageAlt ?? ''}
            loading="eager"
            decoding="async"
            className="absolute inset-0 h-full w-full object-cover opacity-65 transition-transform duration-1000 ease-out hover:scale-105"
          />
          <div aria-hidden="true" className="absolute inset-0 bg-gradient-to-r from-ink/95 via-ink/80 to-ink/30" />
        </>
      )}
      <div className="relative mx-auto flex max-w-6xl flex-col justify-end px-5 py-24 md:py-36">
        <h1 className="max-w-3xl text-balance text-4xl font-semibold leading-tight tracking-tight md:text-5xl lg:text-6xl">
          {pick(slogan, locale)}
        </h1>
        <p className="mt-6 max-w-2xl text-base leading-relaxed text-on-dark/85 md:text-lg">{pick(lead, locale)}</p>

        {cta && (
          <div className="mt-8">
            <GoLink
              to={cta.to}
              className="inline-flex items-center gap-2 rounded-lg bg-primary px-6 py-3 text-sm font-semibold text-on-dark shadow-sm transition-all hover:bg-primary-light hover:shadow-md"
            >
              <span>{pick(cta.label, locale)}</span>
              <RiIcon name="ri-arrow-right-line" className="text-base" />
            </GoLink>
          </div>
        )}

        {stats && stats.length > 0 && (
          <div className="mt-14 grid grid-cols-2 gap-4 border-t border-on-dark/20 pt-8 sm:grid-cols-4">
            {stats.map((s, idx) => (
              <div key={idx}>
                <div className="tnum text-2xl font-bold tracking-tight text-on-dark md:text-3xl">{s.value}</div>
                <div className="mt-1 text-xs text-on-dark/70">{pick(s.label, locale)}</div>
              </div>
            ))}
          </div>
        )}

        {imageCredit && (
          <div className="mt-6 flex items-center gap-1.5 text-xs text-on-dark/65">
            <RiIcon name="ri-camera-lens-line" className="text-sm opacity-80" />
            <span>{imageCredit}</span>
          </div>
        )}
      </div>
    </section>
  );
}

/** 现代卡片网格容器 */
export function CardGrid({
  children,
  cols = 3,
  className = '',
}: {
  children: ReactNode;
  cols?: 2 | 3 | 4;
  className?: string;
}) {
  const colClass = {
    2: 'sm:grid-cols-2',
    3: 'sm:grid-cols-2 lg:grid-cols-3',
    4: 'sm:grid-cols-2 lg:grid-cols-4',
  }[cols];
  return <div className={`grid gap-6 ${colClass} ${className}`}>{children}</div>;
}

/** 现代特性/业务大卡片：悬浮微上浮 + 平滑边框变化 */
export function FeatureCard({
  icon,
  title,
  body,
  link,
  className = '',
}: {
  icon?: string;
  title: BiText;
  body: BiText;
  link?: { label: BiText; to: string };
  className?: string;
}) {
  const { locale } = useI18n();
  return (
    <div
      className={`group flex flex-col justify-between rounded-2xl border border-line bg-bg p-6 shadow-sm transition-all duration-300 hover:-translate-y-1 hover:border-primary/40 hover:shadow-md ${className}`}
    >
      <div>
        {icon && (
          <div className="mb-4 inline-flex h-11 w-11 items-center justify-center rounded-xl bg-primary/10 text-xl text-primary transition-colors group-hover:bg-primary group-hover:text-on-dark">
            <RiIcon name={icon} />
          </div>
        )}
        <h3 className="text-lg font-semibold text-body transition-colors group-hover:text-primary-light">
          {pick(title, locale)}
        </h3>
        <p className="mt-2.5 text-sm leading-relaxed text-muted">{pick(body, locale)}</p>
      </div>
      {link && (
        <div className="mt-6 pt-4 border-t border-line/60">
          <GoLink to={link.to} className="inline-flex items-center gap-1.5 text-xs font-semibold text-primary-light transition-colors group-hover:underline">
            <span>{pick(link.label, locale)}</span>
            <RiIcon name="ri-arrow-right-line" className="transition-transform group-hover:translate-x-1" />
          </GoLink>
        </div>
      )}
    </div>
  );
}

/** 错误状态块 */
export function ErrorState({ title, hint, onRetry }: { title: string; hint?: string; onRetry?: () => void }) {
  return (
    <div className="mx-auto my-12 max-w-lg rounded-2xl border border-danger/30 bg-danger/5 p-6 text-center">
      <RiIcon name="ri-error-warning-line" className="mx-auto mb-2 text-3xl text-danger" />
      <h2 className="text-base font-semibold text-danger-light">{title}</h2>
      {hint && <p className="mt-1.5 text-xs leading-relaxed text-muted">{hint}</p>}
      {onRetry && (
        <button
          onClick={onRetry}
          className="mt-4 inline-flex items-center gap-1.5 rounded-lg border border-line bg-bg px-4 py-1.5 text-xs font-semibold text-body shadow-sm hover:bg-surface-2"
        >
          <RiIcon name="ri-restart-line" className="text-xs" />
          <span>重试</span>
        </button>
      )}
    </div>
  );
}

/** 单一公司品牌 Hero */
export function BrandHero({
  eyebrow,
  title,
  lead,
  backUrl,
  backLabel,
}: {
  eyebrow: BiText;
  title: BiText;
  lead: BiText;
  backUrl: string;
  backLabel: BiText;
}) {
  const { locale } = useI18n();
  return (
    <section className="relative w-full border-b border-line bg-surface py-20 md:py-28 text-center">
      <div className="mx-auto max-w-3xl px-5">
        <div className="inline-flex items-center gap-1.5 rounded-full border border-line bg-bg px-3.5 py-1 text-xs font-semibold tracking-wider text-muted shadow-sm">
          <span className="h-1.5 w-1.5 rounded-full bg-primary" />
          {pick(eyebrow, locale)}
        </div>
        <h1 className="mt-6 text-3xl font-semibold leading-tight tracking-tight text-body md:text-5xl">{pick(title, locale)}</h1>
        <p className="mx-auto mt-5 max-w-xl text-base leading-relaxed text-muted md:text-lg">{pick(lead, locale)}</p>
        <a
          href={backUrl}
          className="mt-8 inline-flex items-center gap-2 rounded-lg border border-line-strong bg-bg px-5 py-2.5 text-sm font-semibold text-body shadow-sm transition-all hover:border-primary hover:text-primary-light hover:shadow"
        >
          <RiIcon name="ri-arrow-left-line" className="text-sm" />
          <span>{pick(backLabel, locale)}</span>
        </a>
      </div>
    </section>
  );
}

/** 渲染期错误边界 */
class RenderBoundary extends Component<{ children: ReactNode; locale: string }, { error: Error | null }> {
  state: { error: Error | null } = { error: null };
  static getDerivedStateFromError(error: Error) {
    return { error };
  }
  render() {
    const { error } = this.state;
    if (!error) return this.props.children;
    const zh = this.props.locale !== 'en';
    return (
      <div className="mx-auto max-w-6xl px-5 py-24">
        <div className="flex items-center gap-2 text-danger-light">
          <RiIcon name="ri-alert-line" className="text-2xl" />
          <h1 className="text-2xl font-semibold text-body">
            {zh ? '页面内容加载出错' : 'Something went wrong rendering this page'}
          </h1>
        </div>
        <p className="mt-4 text-sm leading-relaxed text-muted">
          {zh
            ? '页面数据或组件引用了缺失的内容。请把下面的信息反馈给技术方，我们会尽快修复。'
            : 'Page data or a component referenced something missing. Please pass the detail below to the technical team.'}
        </p>
        <pre className="mt-6 overflow-x-auto rounded-xl border border-line bg-surface p-4 text-xs text-danger-light">
          {String(error.message || error)}
        </pre>
      </div>
    );
  }
}

export function ErrorBoundary({ children }: { children: ReactNode }) {
  const { locale } = useI18n();
  return <RenderBoundary locale={locale}>{children}</RenderBoundary>;
}

export function CorporateShell({
  brand,
  nav,
  localeToggle,
  homeLabel,
  subsidiaries,
  footerNote,
  footerSite,
  skipLabel,
  backLink,
  children,
}: {
  /** mark 为 RemixIcon 类名（如 ri-movie-2-line），不再是文字字标 */
  brand: { name: BiText; mark: string };
  nav: NavEntry[];
  localeToggle: ReactNode;
  homeLabel: BiText;
  subsidiaries: SubsidiaryLink[];
  footerNote: BiText;
  footerSite: string;
  skipLabel: BiText;
  backLink?: { url: string; label: BiText };
  children: ReactNode;
}) {
  const { locale } = useI18n();
  return (
    <div className="flex min-h-screen flex-col overflow-x-clip bg-bg text-body selection:bg-primary/10 selection:text-primary">
      <a
        href="#main"
        className="sr-only focus:not-sr-only focus:absolute focus:left-4 focus:top-4 focus:z-50 focus:rounded-md focus:bg-bg focus:px-3 focus:py-2 focus:text-sm focus:text-body focus:shadow-md"
      >
        {pick(skipLabel, locale)}
      </a>
      {backLink && (
        <div className="border-b border-line bg-surface/90">
          <div className="mx-auto max-w-6xl px-5 py-2">
            <a href={backLink.url} className="inline-flex items-center gap-1.5 text-xs text-muted transition-colors hover:text-primary-light">
              <RiIcon name="ri-arrow-left-line" className="text-xs" />
              <span>{pick(backLink.label, locale)}</span>
            </a>
          </div>
        </div>
      )}
      <SiteHeader brand={brand} nav={nav} localeToggle={localeToggle} homeLabel={homeLabel} />
      <main id="main" className="flex-1">
        {children}
      </main>
      <SiteFooter subsidiaries={subsidiaries} note={footerNote} site={footerSite} />
    </div>
  );
}
